import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import AccountInfoCard from '../components/AccountInfoCard';
import SubscriptionCard from '../components/SubscriptionCard';
import DangerZoneCard from '../components/DangerZoneCard';
import { LogOut, Check } from 'lucide-react';
import { toast } from 'sonner';

const HTTP_UNAUTHORIZED = 401;
const NICKNAME_OPTIONS = ['kid', 'bud', 'champ', 'boss', 'sweetheart', 'darling', 'princess', 'honey', 'baby', 'sugar', 'love', 'dear', 'friend', 'fam', 'bro', 'sis', 'young one'];

const ProfilePage = () => {
  const { logoutUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    try {
      const response = await api.get('/user/profile');
      setProfile(response.data);
    } catch (error) {
      if (error.response?.status !== HTTP_UNAUTHORIZED) {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleAccountDeleted = async () => {
    await logoutUser();
    navigate('/auth');
  };

  const handleLogout = async () => {
    await logoutUser();
    navigate('/auth');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12 px-4 flex items-center justify-center">
        <p className="text-[#A1A1AA]">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light tracking-tighter mb-4" style={{ fontFamily: 'Outfit' }}>
            Your <span className="text-[#8B5CF6]">Profile</span>
          </h1>
          <p className="text-lg text-[#A1A1AA]">Manage your account and preferences</p>
        </div>
        <div className="grid gap-6">
          <AccountInfoCard profile={profile} />
          <Card className="card-surface border-[#27272A]" data-testid="nickname-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit' }}>What Should We Call You?</CardTitle>
              <CardDescription>Pick how your person addresses you</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-3">
                <Input
                  value={profile?.nickname || ''}
                  onChange={(e) => setProfile(prev => ({ ...prev, nickname: e.target.value }))}
                  onBlur={async () => {
                    if (profile?.nickname) {
                      try {
                        await api.put('/user/nickname', { nickname: profile.nickname });
                        toast.success(`Got it — we'll call you "${profile.nickname}" from now on`);
                      } catch { toast.error('Failed to update'); }
                    }
                  }}
                  placeholder="Type your name or pick one below"
                  className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#8B5CF6]"
                  data-testid="profile-nickname-input"
                />
              </div>
              <div className="flex flex-wrap gap-2" data-testid="nickname-editor">
                {NICKNAME_OPTIONS.map((n) => (
                  <button
                    key={n}
                    onClick={async () => {
                      try {
                        await api.put('/user/nickname', { nickname: n });
                        setProfile(prev => ({ ...prev, nickname: n }));
                        toast.success(`Got it — we'll call you "${n}" from now on`);
                      } catch { toast.error('Failed to update'); }
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-1 ${profile?.nickname === n ? 'bg-[#8B5CF6] border-[#8B5CF6] text-white' : 'bg-[#0A0A0A] border-[#27272A] text-[#A1A1AA] hover:border-[#8B5CF6]'}`}
                    data-testid={`edit-nickname-${n}`}
                  >
                    {profile?.nickname === n && <Check className="w-3 h-3" />}
                    {n}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
          <SubscriptionCard profile={profile} />
          <Card className="card-surface border-[#27272A]" data-testid="account-actions-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit' }}>Account Actions</CardTitle>
              <CardDescription>Manage your account</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleLogout} variant="outline" className="w-full rounded-full border-[#27272A] hover:bg-[#1E1E1E]" data-testid="profile-logout-button">
                <LogOut className="w-4 h-4 mr-2" />Log Out
              </Button>
            </CardContent>
          </Card>
          <DangerZoneCard onAccountDeleted={handleAccountDeleted} />
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
